import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ImageIcon, Loader2, Download, AlertCircle, CheckCircle2 } from 'lucide-react';
import { BIOSDK_API_BASE_URL } from './config';
import {
  extractFusedFaceTemplate,
  extractGroupFaceTemplates,
  templateB64ToBlob,
} from './faceAdvancedExtract';
import MultiFileUpload from './components/MultiFileUpload';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { Badge } from './components/ui/badge';
import RoutePreviewChip from './components/RoutePreviewChip';

function FaceFusionLab() {
  const [fusedFiles, setFusedFiles] = useState([]);
  const [groupFile, setGroupFile] = useState(null);
  const [fusedResult, setFusedResult] = useState(null);
  const [groupResult, setGroupResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');

  const fusedUrl = `${BIOSDK_API_BASE_URL.replace(/\/$/, '')}/face/extract-fused-template`;
  const groupUrl = `${BIOSDK_API_BASE_URL.replace(/\/$/, '')}/face/extract-group-faces`;

  const runFused = async () => {
    setError(null);
    setFusedResult(null);
    if (fusedFiles.length < 1) {
      setError('Upload at least one image for fused extract (2+ recommended).');
      return;
    }
    setBusy('fused');
    try {
      const data = await extractFusedFaceTemplate(BIOSDK_API_BASE_URL, fusedFiles);
      if (data.failed || data.success === false) {
        throw new Error(data.error || 'Fused extract failed');
      }
      setFusedResult(data);
    } catch (exc) {
      setError(exc?.response?.data?.error || exc.message || String(exc));
    } finally {
      setBusy('');
    }
  };

  const runGroup = async () => {
    setError(null);
    setGroupResult(null);
    if (!groupFile) {
      setError('Upload a group photo.');
      return;
    }
    setBusy('group');
    try {
      const data = await extractGroupFaceTemplates(BIOSDK_API_BASE_URL, groupFile);
      if (data.failed && !data.templates?.length) {
        throw new Error(data.error || 'Group extract failed');
      }
      setGroupResult(data);
    } catch (exc) {
      setError(exc?.response?.data?.error || exc.message || String(exc));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container-wide space-y-6">
        <Card className="biochq-card face">
          <CardHeader>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Users className="h-8 w-8 text-face" />
              <CardTitle className="text-3xl font-bold text-gray-800">Face fusion lab</CardTitle>
            </div>
            <CardDescription className="text-center text-gray-600">
              Fused enrollment and group-photo extraction via biosdk-service{' '}
              <code>/face/extract-fused-template</code> and <code>/face/extract-group-faces</code> only.
              Download BCHQ templates; use Matcher for 1:1 comparison.
            </CardDescription>
            <div className="flex justify-center mt-2">
              <Link to="/extractor" className="text-sm text-blue-600 hover:underline">
                ← Back to face extractor
              </Link>
            </div>
          </CardHeader>
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Table 5 — fused template (K images)</CardTitle>
            <RoutePreviewChip url={fusedUrl} label="POST /face/extract-fused-template" />
          </CardHeader>
          <CardContent className="space-y-4">
            <MultiFileUpload
              files={fusedFiles}
              onChange={setFusedFiles}
              multiple
              accept="image/jpeg,image/png,image/webp"
              label="Enrollment images (same person)"
            />
            <Button onClick={runFused} disabled={busy === 'fused'}>
              {busy === 'fused' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Extract fused template
            </Button>
            {fusedResult && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Fused extract OK</AlertTitle>
                <AlertDescription className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <Badge>faces: {fusedResult.face_count ?? '—'}</Badge>
                    <Badge>
                      time: {fusedResult.processing_time != null ? `${fusedResult.processing_time}s` : '—'}
                    </Badge>
                  </div>
                  {fusedResult.template_b64 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const blob = templateB64ToBlob(fusedResult.template_b64);
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = 'fused-face-template.bchq';
                        a.click();
                      }}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download BCHQ template
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Table 6 — group photo (M faces)</CardTitle>
            <RoutePreviewChip url={groupUrl} label="POST /face/extract-group-faces" />
          </CardHeader>
          <CardContent className="space-y-4">
            <MultiFileUpload
              files={groupFile ? [groupFile] : []}
              onChange={(files) => setGroupFile(files[0] || null)}
              multiple={false}
              maxFiles={1}
              accept="image/jpeg,image/png,image/webp"
              label="Group photo"
            />
            <Button onClick={runGroup} disabled={busy === 'group'}>
              {busy === 'group' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Extract group faces
            </Button>
            {groupResult && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Group extract OK</AlertTitle>
                <AlertDescription className="space-y-3">
                  <Badge>
                    <ImageIcon className="mr-1 h-3 w-3 inline" />
                    {groupResult.face_count ?? groupResult.templates?.length ?? 0} faces
                  </Badge>
                  <ul className="text-sm space-y-1">
                    {(groupResult.templates || []).map((row) => (
                      <li key={row.face_index} className="flex items-center gap-2">
                        <span>
                          Face {row.face_index}
                          {row.failed ? ' (failed)' : ''}
                          {row.x != null ? ` · bbox ${row.x},${row.y} ${row.w}×${row.h}` : ''}
                        </span>
                        {row.template_b64 && !row.failed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const blob = templateB64ToBlob(row.template_b64);
                              const a = document.createElement('a');
                              a.href = URL.createObjectURL(blob);
                              a.download = `group-face-${row.face_index}.bchq`;
                              a.click();
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default FaceFusionLab;
